import { useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  CoachMark,
  Flex,
  Heading,
  HoverCard,
  Paragraph,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import { usePublishAccessScope } from './usePublishAccessScope'
import { ViewChannelPermissionsActions } from './ViewChannelPermissionsActions'

import styles from './ViewOnlyCoachMarks.module.css'

/**
 * Server-persisted, per-account flag (via `account.dismissedBanners`) for the
 * one-time view-only coach mark. Publish, Create, Insights and Community all
 * read this one flag, so the coach mark shows in the first area a view-only
 * actor visits and never again after they acknowledge it. The value keeps
 * its original Publish name so earlier dismissals still count.
 */
export const VIEW_ONLY_COACH_MARK_BANNER = 'publish-view-only-coach-mark'

const TITLE = 'You have view-only access to Publish on this channel'
const BODY =
  'You can see posts, drafts, and leave notes. To create, edit, or move ' +
  'posts, ask an admin to update your permissions.'

export type ViewOnlyCopy = { title: string; body: string }

const PUBLISH_COPY: ViewOnlyCopy = { title: TITLE, body: BODY }

type ViewOnlyPublishCoachMarkProps = {
  /** The View Only badge in the page header the coach mark anchors to. */
  children: ReactNode
  /** What view-only means on the badged surface. Defaults to Publish. */
  copy?: ViewOnlyCopy
}

/**
 * Persistent affordance once the coach mark is gone: the badge reveals the
 * same explanation and actions in a hover card on pointer-hover and keyboard
 * focus. "Got It" just closes the popover — nothing is pending in this state.
 */
const ViewOnlyBadgeHint = ({
  children,
  copy,
}: {
  children: ReactNode
  copy: ViewOnlyCopy
}): JSX.Element => {
  const [open, setOpen] = useState(false)

  return (
    <HoverCard
      open={open}
      onOpenChange={setOpen}
      openDelay="fast"
      closeDelay="fast"
    >
      <HoverCard.Trigger>
        <UnstyledButton className={styles.trigger}>{children}</UnstyledButton>
      </HoverCard.Trigger>
      <HoverCard.Content
        side="bottom"
        align="center"
        className={styles.content}
      >
        <Flex direction="column" gap="md">
          <Flex direction="column" gap="xs">
            <Heading size="sm">{copy.title}</Heading>
            <Paragraph size="md">{copy.body}</Paragraph>
          </Flex>
          <Flex justify="end" gap="xs" fullWidth>
            <ViewChannelPermissionsActions
              onDismiss={(): void => setOpen(false)}
            />
          </Flex>
        </Flex>
      </HoverCard.Content>
    </HoverCard>
  )
}

/**
 * View-only education anchored to the View Only badge in the page header: a
 * one-time coach mark on first visit for actors whose entire Publish access
 * is view-only (either action persists the dismissal per account), then the
 * {@link ViewOnlyBadgeHint} hover card for anyone else the page badges.
 *
 * Mutually exclusive with {@link MixedPublishAccessCoachMark} via
 * {@link usePublishAccessScope}: a mixed-access actor never reaches
 * `allViewOnly`, so their one-time education anchors to the Publish nav item
 * instead.
 */
export const ViewOnlyPublishCoachMark = ({
  children,
  copy = PUBLISH_COPY,
}: ViewOnlyPublishCoachMarkProps): JSX.Element => {
  const scope = usePublishAccessScope()
  const { isActive, dismiss } = useDismissableBanner(
    VIEW_ONLY_COACH_MARK_BANNER,
    { trackDismiss: true },
  )

  if (scope !== 'allViewOnly' || !isActive) {
    return <ViewOnlyBadgeHint copy={copy}>{children}</ViewOnlyBadgeHint>
  }

  return (
    <CoachMark open onDismiss={dismiss} dismissTimeout={null}>
      <CoachMark.Overlay>
        <CoachMark.Spotlight size="small" />
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content
        side="bottom"
        align="center"
        showCloseIcon={false}
        className={styles.content}
      >
        <CoachMark.Title>{copy.title}</CoachMark.Title>
        <CoachMark.Description>{copy.body}</CoachMark.Description>
        <CoachMark.Footer>
          <ViewChannelPermissionsActions onDismiss={dismiss} />
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
